import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, CalendarDays, UserPlus } from "lucide-react";
import InviteDialog from "@/components/InviteDialog";
import { useNavigate } from "react-router-dom";

const STATUS = {
  new: { label: "À rappeler", cls: "bg-[#E0C173]/20 text-[#8a6d22] border-[#E0C173]" },
  scheduled: { label: "RDV planifié", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  done: { label: "RDV réalisé", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};
const MODE = { telephone: "Téléphone", visio: "Visio", bureau: "Au bureau" };
const SLOT = { matin: "matin", "apres-midi": "après-midi", indifferent: "créneau indifférent" };

const Detail = ({ k, v }) => (v ? <div><span className="text-gray-500">{k} : </span><span className="text-[#1B2A4A] font-medium">{v}</span></div> : null);

const Item = ({ r, onStatus, onInvite, onOnsite }) => (
  <div data-testid={`rdv-request-${r.request_id}`} className={`bg-white border p-5 ${r.status === "new" ? "border-l-4 border-l-[#E0C173] border-gray-200" : "border-gray-200"}`}>
    <div className="flex justify-between flex-wrap gap-2">
      <div className="font-medium text-[#1B2A4A] flex flex-wrap items-center gap-x-3 gap-y-1">
        {r.name}
        <a href={`mailto:${r.email}`} className="text-sm font-normal text-[#475569] inline-flex items-center gap-1 hover:text-[#1B2A4A]"><Mail className="w-3.5 h-3.5" />{r.email}</a>
        <a href={`tel:${r.phone}`} className="text-sm font-normal text-[#475569] inline-flex items-center gap-1 hover:text-[#1B2A4A]"><Phone className="w-3.5 h-3.5" />{r.phone}</a>
      </div>
      <div className="text-xs text-gray-500">{new Date(r.created_at).toLocaleString("fr-FR")}</div>
    </div>
    <div className="mt-3 inline-flex items-center gap-2 text-sm bg-[#F8F6F0] px-3 py-1.5">
      <CalendarDays className="w-4 h-4 text-[#C9A85A]" /> {MODE[r.meeting_mode]} · {r.preferred_date ? new Date(r.preferred_date).toLocaleDateString("fr-FR") : "date libre"} · {SLOT[r.preferred_slot]}
    </div>
    <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 text-sm">
      <Detail k="Secteur" v={r.company_sector} /><Detail k="Département" v={r.region} /><Detail k="CA" v={r.revenue_range} />
      <Detail k="Effectif" v={r.employees_range} /><Detail k="Horizon" v={r.timing} />
    </div>
    {r.message && <p className="mt-3 text-sm text-[#475569] whitespace-pre-line border-l-2 border-[#E0C173]/60 pl-3">{r.message}</p>}
    <div className="mt-4 flex flex-wrap gap-2">
      {Object.entries(STATUS).map(([k, s]) => (
        <button key={k} data-testid={`rdv-status-${k}-${r.request_id}`} onClick={() => r.status !== k && onStatus(r.request_id, k)}
          className={`text-xs px-3 py-1 border transition-colors ${r.status === k ? s.cls + " font-semibold" : "border-gray-300 text-gray-600 hover:text-[#1B2A4A] hover:border-gray-400"}`}>{s.label}</button>
      ))}
      <button data-testid={`rdv-onsite-${r.request_id}`} onClick={() => onOnsite(r)} className="ml-auto inline-flex items-center gap-1.5 text-xs px-3 py-1 bg-[#E0C173] text-[#1B2A4A] font-semibold hover:bg-[#C9A85A] transition-colors"><CalendarDays className="w-3.5 h-3.5" /> Démarrer pendant le RDV</button>
      <button data-testid={`rdv-invite-${r.request_id}`} onClick={() => onInvite(r)} className=" inline-flex items-center gap-1.5 text-xs px-3 py-1 bg-[#1B2A4A] text-white hover:bg-[#2a3d63] transition-colors"><UserPlus className="w-3.5 h-3.5" /> Inviter à créer son espace cédant</button>
    </div>
  </div>
);

export default function DiscoveryRequestsPanel({ onChange }) {
  const [items, setItems] = useState([]);
  const [inviting, setInviting] = useState(null);
  const navigate = useNavigate();
  const onOnsite = (r) => navigate(`/admin/rdv?${new URLSearchParams({ rdv: r.request_id, name: r.name, email: r.email, phone: r.phone || "" })}`);
  const load = () => api.get("/admin/discovery-requests").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const onStatus = async (id, status) => {
    try { await api.patch(`/admin/discovery-requests/${id}/status`, { status }); toast.success(`Statut : ${STATUS[status].label}`); load(); onChange?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (items.length === 0) return <p data-testid="rdv-requests-empty" className="text-gray-500 py-10 text-center">Aucune demande de RDV découverte pour le moment.</p>;
  return (
    <div data-testid="rdv-requests" className="space-y-3">
      {items.map((r) => <Item key={r.request_id} r={r} onStatus={onStatus} onInvite={setInviting} onOnsite={onOnsite} />)}
      <InviteDialog open={!!inviting} onOpenChange={(v) => !v && setInviting(null)} prefill={inviting && { name: inviting.name, email: inviting.email }} />
    </div>
  );
}
